import { useTranslation } from 'react-i18next';
import { Bus, ChevronLeft, ChevronRight, Plane } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import type { CalendarEvent, CalendarStatus } from '../calendarModel';
import type { WeekSegment } from '../laneLayout';

const STATUS_BAR_CLASSES: Record<CalendarStatus, string> = {
  draft: 'border-l-phx-draft bg-phx-draft-tint',
  contracted: 'border-l-phx-warning bg-phx-warning-tint',
  confirmed: 'border-l-phx-success bg-phx-success-tint',
  canceled: 'border-l-phx-danger bg-phx-danger-tint',
  unknown: 'border-l-phx-border-3 bg-phx-surface-2',
};

function TransportIcon({ transport }: { transport: CalendarEvent['transport'] }) {
  return (
    <span className="text-phx-text-2 flex shrink-0 items-center gap-px">
      {transport !== 'coach' && <Plane className="h-3 w-3" aria-hidden="true" />}
      {transport !== 'air' && <Bus className="h-3 w-3" aria-hidden="true" />}
    </span>
  );
}

interface TermBarProps {
  segment: WeekSegment<CalendarEvent>;
  onClick: (termId: string) => void;
}

/**
 * Pruh jednoho termínu v týdenním řádku. Levý okraj + tint nese stav termínu;
 * bez dopravního schématu se vykreslí placeholder styl („doprava nenaplánována“).
 * Legy hlavní dopravy = chipy na okrajích (outbound vlevo, return vpravo),
 * generované route options = mini-chipy pod titulkem (max 2 + „+N“).
 */
export function TermBar({ segment, onClick }: TermBarProps) {
  const { t } = useTranslation('planner');
  const { item: event, startCol, span, clippedStart, clippedEnd, lane } = segment;

  const schema = event.schema;
  const hasLegs = (schema?.primaryLegs.length ?? 0) > 0;
  const outboundLegs = schema?.primaryLegs.filter((leg) => leg.direction === 'Outbound') ?? [];
  const inboundLegs = schema?.primaryLegs.filter((leg) => leg.direction === 'Inbound') ?? [];
  const routeOptions = schema?.routeOptions ?? [];
  const visibleOptions = routeOptions.slice(0, 2);
  const hiddenOptionsCount = routeOptions.length - visibleOptions.length;

  return (
    <button
      type="button"
      data-testid="calendar-term-bar"
      data-term-id={event.termId}
      onClick={() => onClick(event.termId)}
      title={`${event.name} · ${t('calendar.bar.nights', { count: event.nights })}`}
      className={cn(
        'rounded-phx-sm text-phx-xs text-phx-text pointer-events-auto z-10 mx-px flex min-w-0 cursor-pointer flex-col gap-px border-l-3 px-1 py-px text-left',
        STATUS_BAR_CLASSES[event.status],
        event.status === 'canceled' && 'opacity-60',
        !schema && 'border-phx-border-3 border border-l-3 border-dashed',
        clippedStart && 'rounded-l-none',
        clippedEnd && 'rounded-r-none'
      )}
      style={{ gridColumn: `${startCol + 1} / span ${span}`, gridRow: lane + 2 }}
    >
      <span className="flex min-w-0 items-center gap-1">
        {clippedStart && <ChevronLeft className="h-3 w-3 shrink-0" aria-hidden="true" />}
        {!clippedStart && hasLegs && outboundLegs[0] && (
          <span className="bg-phx-surface text-phx-text-2 rounded-phx-sm border-phx-border shrink-0 border px-1 leading-tight">
            {outboundLegs[0].facilityCode ?? outboundLegs[0].regularTransportName}
          </span>
        )}
        <TransportIcon transport={event.transport} />
        <span
          className={cn(
            'min-w-0 truncate font-medium',
            event.status === 'canceled' && 'line-through'
          )}
        >
          {event.name}
        </span>
        <span className="text-phx-text-muted shrink-0 tabular-nums">
          {t('calendar.bar.nightsShort', { count: event.nights })}
        </span>
        {event.capacity != null && (
          <span className="text-phx-text-muted shrink-0 tabular-nums">· {event.capacity}</span>
        )}
        <span className="min-w-0 flex-1" />
        {!clippedEnd && hasLegs && inboundLegs[0] && (
          <span className="bg-phx-surface text-phx-text-2 rounded-phx-sm border-phx-border shrink-0 border px-1 leading-tight">
            {inboundLegs[0].facilityCode ?? inboundLegs[0].regularTransportName}
          </span>
        )}
        {clippedEnd && <ChevronRight className="h-3 w-3 shrink-0" aria-hidden="true" />}
      </span>

      {!schema && (
        <span className="text-phx-text-muted truncate italic">{t('calendar.bar.noTransport')}</span>
      )}

      {visibleOptions.length > 0 && (
        <span className="flex min-w-0 items-center gap-1">
          {visibleOptions.map((option) => (
            <span
              key={option.routeOptionId}
              className="bg-phx-surface text-phx-text-muted rounded-phx-sm border-phx-border min-w-0 truncate border px-1 leading-tight"
            >
              {option.outboundStationName} ⇄ {option.inboundStationName}
            </span>
          ))}
          {hiddenOptionsCount > 0 && (
            <span className="text-phx-text-muted shrink-0">+{hiddenOptionsCount}</span>
          )}
        </span>
      )}
    </button>
  );
}
